import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { hasConvivenciaAcceptance } from '@/services/convivencia'
import { fetchMyProfile } from '@/services/profile'
import { useAdminModerationStore } from '@/stores/adminModeration'
import { useSuperAdminStore } from '@/stores/superAdmin'
import { useOrgAdminStore } from '@/stores/orgAdmin'
import {
  getLastCompletedStep,
  onboardingRouteForStep,
  resolveOnboardingStep,
} from '@/utils/onboardingProgress'
import { canAccessDiscovery } from '@/utils/discovery'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
      meta: { title: 'JUNTAS — Sororidad para vivir' },
    },
    {
      path: '/registro',
      name: 'register',
      component: () => import('@/views/auth/RegisterView.vue'),
      meta: { title: 'Crear cuenta — JUNTAS', guestOnly: true },
    },
    {
      path: '/iniciar-sesion',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { title: 'Iniciar sesión — JUNTAS', guestOnly: true },
    },
    {
      path: '/recuperar-contrasena',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { title: 'Recuperar contraseña — JUNTAS', guestOnly: true },
    },
    {
      path: '/onboarding/bienvenida',
      name: 'onboarding-welcome',
      component: () => import('@/views/onboarding/OnboardingWelcomeView.vue'),
      meta: { title: 'Bienvenida — JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-1',
      name: 'onboarding-step-1',
      component: () => import('@/views/onboarding/OnboardingStep1View.vue'),
      meta: { title: 'Paso 1 — Sobre ti · JUNTAS' },
    },
    {
      path: '/onboarding/paso-2',
      name: 'onboarding-step-2',
      component: () => import('@/views/onboarding/OnboardingStep2View.vue'),
      meta: { title: 'Paso 2 — Familia · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-3',
      name: 'onboarding-step-3',
      component: () => import('@/views/onboarding/OnboardingStep3View.vue'),
      meta: { title: 'Paso 3 — Horarios · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-4',
      name: 'onboarding-step-4',
      component: () => import('@/views/onboarding/OnboardingStep4View.vue'),
      meta: { title: 'Paso 4 — Estilo de vida · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-5',
      name: 'onboarding-step-5',
      component: () => import('@/views/onboarding/OnboardingStep5View.vue'),
      meta: { title: 'Paso 5 — Preferencias · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-6',
      name: 'onboarding-step-6',
      component: () => import('@/views/onboarding/OnboardingStep6View.vue'),
      meta: { title: 'Paso 6 — Verificación · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/paso-7',
      name: 'onboarding-step-7',
      component: () => import('@/views/onboarding/OnboardingStep7View.vue'),
      meta: { title: 'Paso 7 — Carta de Convivencia · JUNTAS', requiresAuth: true },
    },
    {
      path: '/onboarding/completado',
      name: 'onboarding-complete',
      component: () => import('@/views/onboarding/OnboardingCompleteView.vue'),
      meta: { title: 'Perfil enviado — JUNTAS', requiresAuth: true },
    },
    {
      path: '/mi-perfil',
      name: 'my-profile',
      component: () => import('@/views/MyProfileView.vue'),
      meta: { title: 'Mi perfil — JUNTAS', requiresAuth: true },
    },
    {
      path: '/mi-perfil/editar',
      name: 'profile-edit',
      component: () => import('@/views/ProfileEditView.vue'),
      meta: { title: 'Editar perfil — JUNTAS', requiresAuth: true },
    },
    {
      path: '/buscar',
      name: 'discovery',
      component: () => import('@/views/discovery/DiscoveryView.vue'),
      meta: {
        title: 'Compañeras compatibles — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/vivienda',
      name: 'marketplace',
      component: () => import('@/views/marketplace/MarketplaceView.vue'),
      meta: {
        title: 'Viviendas — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/candidaturas/:candidaturaId',
      name: 'candidatura',
      component: () => import('@/views/marketplace/CandidaturaDetailView.vue'),
      meta: {
        title: 'Candidatura — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/propietarios',
      name: 'propio-panel',
      component: () => import('@/views/propio/PropioPanelView.vue'),
      meta: {
        title: 'Panel propietario aliado — JUNTAS',
        requiresAuth: true,
      },
    },
    {
      path: '/propietarios/mis-ofertas',
      name: 'propio-mis-ofertas',
      component: () => import('@/views/propio/PropioMisOfertasView.vue'),
      meta: {
        title: 'Mis ofertas — Panel propietario aliado — JUNTAS',
        requiresAuth: true,
      },
    },
    {
      path: '/propietarios/sello',
      name: 'propio-sello',
      component: () => import('@/views/propio/PropioSelloView.vue'),
      meta: {
        title: 'Sello Vivienda Sororal — Panel propietario aliado — JUNTAS',
        requiresAuth: true,
      },
    },
    {
      path: '/propietarios/fiscal',
      name: 'propio-fiscal',
      component: () => import('@/views/propio/PropioFiscalView.vue'),
      meta: {
        title: 'Beneficio fiscal LIRPF — Panel propietario aliado — JUNTAS',
        requiresAuth: true,
      },
    },
    {
      path: '/propietarios/candidaturas/:candidaturaId',
      name: 'propio-candidatura',
      component: () => import('@/views/propio/PropioCandidaturaDetailView.vue'),
      meta: {
        title: 'Candidatura — Panel propietario aliado — JUNTAS',
        requiresAuth: true,
      },
    },
    {
      path: '/carta-propietarios',
      name: 'carta-propietarios',
      component: () => import('@/views/CartaPropietariosView.vue'),
      meta: {
        title: 'Carta JUNTAS Propietarios — JUNTAS',
      },
    },
    {
      path: '/propietarias',
      redirect: { name: 'propio-panel' },
    },
    {
      path: '/propietarias/candidaturas/:candidaturaId',
      redirect: (to) => ({
        name: 'propio-candidatura',
        params: { candidaturaId: to.params.candidaturaId },
      }),
    },
    {
      path: '/vivienda/:ofertaId',
      name: 'marketplace-offer',
      component: () => import('@/views/marketplace/OfferDetailView.vue'),
      meta: {
        title: 'Oferta de vivienda — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/conversaciones',
      name: 'conversations',
      component: () => import('@/views/chat/ConversationsListView.vue'),
      meta: {
        title: 'Conversaciones — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/conversaciones/grupos/nuevo',
      name: 'piso-group-create',
      component: () => import('@/views/chat/PisoGroupCreateView.vue'),
      meta: {
        title: 'Crear grupo de piso — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/conversaciones/grupos/:groupId',
      name: 'piso-group',
      component: () => import('@/views/chat/PisoGroupDetailView.vue'),
      meta: {
        title: 'Grupo de piso — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    {
      path: '/conversaciones/:id',
      name: 'conversation',
      component: () => import('@/views/chat/ConversationDetailView.vue'),
      meta: {
        title: 'Conversación — JUNTAS',
        requiresAuth: true,
        requiresDiscovery: true,
      },
    },
    // ── Pages institutionnelles (publiques) ─────────────────────────────
    {
      path: '/ajustes',
      name: 'ajustes',
      component: () => import('@/views/AjustesView.vue'),
      meta: { title: 'Ajustes — JUNTAS', requiresAuth: true },
    },
    {
      path: '/cita/cancelar',
      name: 'cita-cancelar',
      component: () => import('@/views/CancelViaCView.vue'),
      meta: { title: 'Cancelar videollamada — JUNTAS', requiresAuth: true },
    },
    {
      path: '/recursos',
      name: 'recursos',
      component: () => import('@/views/RecursosView.vue'),
      meta: { title: 'Recursos de ayuda — JUNTAS' },
    },
    {
      path: '/transparencia',
      name: 'transparencia',
      component: () => import('@/views/TransparenciaView.vue'),
      meta: { title: 'Transparencia — JUNTAS' },
    },
    {
      path: '/privacidad',
      name: 'privacidad',
      component: () => import('@/views/PrivacidadView.vue'),
      meta: { title: 'Política de privacidad — JUNTAS' },
    },
    {
      path: '/aviso-legal',
      name: 'aviso-legal',
      component: () => import('@/views/AvisoLegalView.vue'),
      meta: { title: 'Aviso legal — JUNTAS' },
    },
    {
      path: '/manifiesto',
      name: 'manifiesto',
      component: () => import('@/views/ManifiestoView.vue'),
      meta: { title: 'Nuestro manifiesto — JUNTAS' },
    },
    {
      path: '/carta-juntas',
      name: 'carta-juntas',
      component: () => import('@/views/CartaJuntasView.vue'),
      meta: { title: 'Cartas JUNTAS' },
    },
    {
      path: '/para-propietarios',
      name: 'landing-propietarios',
      component: () => import('@/views/PropietariosLandingView.vue'),
      meta: { title: 'Propietarios aliados — JUNTAS' },
    },
    {
      path: '/para-asociaciones',
      name: 'landing-asociaciones',
      component: () => import('@/views/AsociacionesLandingView.vue'),
      meta: { title: 'Asociaciones aliadas — JUNTAS' },
    },
    // ────────────────────────────────────────────────────────────────────
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
      meta: { title: 'Conexión — JUNTAS' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { title: 'Nueva contraseña — JUNTAS' },
    },
    {
      path: '/admin',
      name: 'admin-dashboard',
      component: () => import('@/views/admin/AdminDashboardView.vue'),
      meta: { title: 'Admin · Dashboard — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/admin/reportes',
      name: 'admin-reportes',
      component: () => import('@/views/admin/AdminReportesView.vue'),
      meta: { title: 'Admin · Reportes — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/super-admin/moderadoras',
      name: 'super-admin-moderadoras',
      component: () => import('@/views/super-admin/SuperAdminModeradasView.vue'),
      meta: { title: 'Super admin · Moderadoras — JUNTAS', requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/super-admin/usuarias',
      name: 'super-admin-usuarias',
      component: () => import('@/views/super-admin/SuperAdminUsuariasView.vue'),
      meta: { title: 'Super admin · Usuarias — JUNTAS', requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/super-admin/propios',
      name: 'super-admin-propios',
      component: () => import('@/views/super-admin/SuperAdminPropiosView.vue'),
      meta: { title: 'Super admin · Propietarios — JUNTAS', requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/super-admin/revocaciones',
      name: 'super-admin-revocaciones',
      component: () => import('@/views/super-admin/SuperAdminRevocacionesView.vue'),
      meta: { title: 'Super admin · Revocar KYC — JUNTAS', requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/admin/kyc',
      name: 'admin-kyc',
      component: () => import('@/views/admin/AdminKycView.vue'),
      meta: { title: 'Admin · Verificaciones usuarias — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/admin/usuarias',
      name: 'admin-usuarias',
      component: () => import('@/views/admin/AdminUsuariasView.vue'),
      meta: { title: 'Admin · Usuarias — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/admin/agenda',
      name: 'admin-agenda',
      component: () => import('@/views/admin/AdminAgendaView.vue'),
      meta: { title: 'Admin · Agenda de verificaciones — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/admin/usuarias/:id/editar',
      name: 'admin-usuaria-editar',
      component: () => import('@/views/admin/AdminUsuariaEditView.vue'),
      meta: { title: 'Admin · Editar usuaria — JUNTAS', requiresAuth: true, requiresModerator: true },
    },
    {
      path: '/admin/propietarios',
      name: 'admin-propio-kyc',
      component: () => import('@/views/admin/AdminPropioKycView.vue'),
      meta: {
        title: 'Admin · Verificaciones propietarios aliados — JUNTAS',
        requiresAuth: true,
        requiresModerator: true,
      },
    },
    {
      path: '/admin/propietarias',
      redirect: { name: 'admin-propio-kyc' },
    },
    {
      path: '/admin/asociaciones',
      name: 'admin-asociaciones',
      component: () => import('@/views/admin/AdminAsociacionesView.vue'),
      meta: {
        title: 'Admin · Asociaciones — JUNTAS',
        requiresAuth: true,
        requiresModerator: true,
      },
    },
    {
      path: '/admin/dossiers',
      name: 'admin-dossiers',
      component: () => import('@/views/admin/AdminDossiersView.vue'),
      meta: {
        title: 'Admin · Dossiers — JUNTAS',
        requiresAuth: true,
        requiresModerator: true,
      },
    },
    {
      path: '/admin/mediacion',
      name: 'admin-mediacion',
      component: () => import('@/views/admin/AdminMediacionView.vue'),
      meta: {
        title: 'Admin · Mediación — JUNTAS',
        requiresAuth: true,
        requiresModerator: true,
      },
    },
    {
      path: '/admin/sello',
      name: 'admin-sello',
      component: () => import('@/views/admin/AdminSelloView.vue'),
      meta: {
        title: 'Admin · Sello Vivienda Sororal — JUNTAS',
        requiresAuth: true,
        requiresModerator: true,
      },
    },
    // ── Pages usuaria S6 ─────────────────────────────────────────────
    {
      path: '/mis-bloqueos',
      name: 'mis-bloqueos',
      component: () => import('@/views/MisBloqueos.vue'),
      meta: { title: 'Mis bloqueos — JUNTAS', requiresAuth: true },
    },
    {
      path: '/mis-reportes',
      name: 'mis-reportes',
      component: () => import('@/views/MisReportesView.vue'),
      meta: { title: 'Mis reportes — JUNTAS', requiresAuth: true },
    },
    {
      path: '/mis-candidaturas',
      name: 'mis-candidaturas',
      component: () => import('@/views/MisCandidaturasView.vue'),
      meta: { title: 'Mis candidaturas — JUNTAS', requiresAuth: true },
    },
    {
      path: '/asociaciones/panel',
      name: 'asociaciones-panel',
      component: () => import('@/views/asociaciones/AsociacionesPanelView.vue'),
      meta: {
        title: 'Panel asociación — JUNTAS',
        requiresAuth: true,
        requiresOrgAdmin: true,
      },
    },
    {
      path: '/asociaciones/pendiente',
      name: 'asociacion-pending',
      component: () => import('@/views/asociaciones/AsociacionPendingView.vue'),
      meta: { title: 'Cuenta de asociación — JUNTAS', requiresAuth: true },
    },
    {
      path: '/admin/acceso-denegado',
      name: 'admin-denied',
      component: () => import('@/views/admin/AdminAccessDeniedView.vue'),
      meta: { title: 'Acceso restringido — JUNTAS', requiresAuth: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { title: 'Página no encontrada — JUNTAS' },
    },
  ],
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0 }
  },
})

async function isLoggedIn() {
  if (!isSupabaseConfigured || !supabase) return false
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return Boolean(session)
}

router.beforeEach(async (to) => {
  const loggedIn = await isLoggedIn()

  if (to.meta.requiresAuth && !loggedIn) {
    // Page protégée + visiteur déconnecté → connexion (pas inscription).
    // Les nouvelles usuarias passent par l'onboarding (paso-1 → register).
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    }
  }

  if (to.meta.guestOnly && loggedIn) {
    const { data: profile } = await fetchMyProfile()
    const accountType = profile?.account_type ?? 'usuaria'
    if (accountType === 'propietario') return { name: 'propio-panel' }
    if (accountType === 'asociacion') return { name: 'asociacion-pending' }
    let cartaAccepted = false
    try {
      cartaAccepted = await hasConvivenciaAcceptance()
    } catch {
      cartaAccepted = false
    }
    const step = resolveOnboardingStep(profile, cartaAccepted)
    if (step >= 8) return { name: 'home' }
    return onboardingRouteForStep(step)
  }

  if (
    loggedIn &&
    (to.name === 'onboarding-complete' || String(to.name).startsWith('onboarding-step-'))
  ) {
    const { data: profile } = await fetchMyProfile()
    // L'onboarding (genre, KYC femme, Carta de Convivencia) est réservé aux
    // usuarias. Un compte propio/asociación n'y entre pas.
    if (profile?.account_type === 'propietario') return { name: 'propio-panel' }
    if (profile?.account_type === 'asociacion') return { name: 'asociacion-pending' }
    if (profile) {
      let cartaAccepted = false
      try {
        cartaAccepted = await hasConvivenciaAcceptance()
      } catch {
        cartaAccepted = false
      }
      const step = resolveOnboardingStep(profile, cartaAccepted)
      if (to.name === 'onboarding-complete' && step < 8) {
        // Permettre l'accès si l'utilisatrice vient de finir l'étape 7
        // (le profil en base peut être légèrement en retard sur le fetch)
        const lastCompleted = getLastCompletedStep()
        if (lastCompleted >= 7) return undefined
        return onboardingRouteForStep(step)
      }
      const stepMatch = String(to.name).match(/^onboarding-step-(\d+)$/)
      if (stepMatch) {
        const requested = Number(stepMatch[1])
        const lastCompleted = getLastCompletedStep()
        // Autoriser le pas suivant juste après un save (profil parfois en retard sur le fetch)
        const mayAdvanceOneStep = requested <= lastCompleted + 1
        if (requested > step && !mayAdvanceOneStep) {
          return onboardingRouteForStep(step)
        }
      }
    }
  }

  if (to.meta.requiresDiscovery) {
    const { data: profile } = await fetchMyProfile()
    let cartaAccepted = false
    try {
      cartaAccepted = await hasConvivenciaAcceptance()
    } catch {
      cartaAccepted = false
    }
    if (!canAccessDiscovery(profile, cartaAccepted)) {
      if (profile?.identity_status !== 'verified') {
        return { name: 'my-profile', query: { discovery: 'pending' } }
      }
      const step = resolveOnboardingStep(profile, cartaAccepted)
      return onboardingRouteForStep(step)
    }
  }

  if (to.meta.requiresModerator) {
    const adminStore = useAdminModerationStore()
    const isMod = await adminStore.ensureChecked()
    if (!isMod) return { name: 'admin-denied' }
  }

  if (to.meta.requiresSuperAdmin) {
    const superAdminStore = useSuperAdminStore()
    const isSA = await superAdminStore.ensureChecked()
    if (!isSA) return { name: 'admin-denied' }
  }

  if (to.meta.requiresOrgAdmin) {
    const orgAdminStore = useOrgAdminStore()
    const isOA = await orgAdminStore.ensureChecked()
    if (!isOA) return { name: 'admin-denied' }
  }
})

router.afterEach((to) => {
  const discrecion = localStorage.getItem('juntas_discrecion') === '1'
  document.title = discrecion ? 'Inicio' : (to.meta.title ?? 'JUNTAS')

  nextTick(() => {
    const main = document.querySelector('main')
    if (!main) return
    if (!main.getAttribute('tabindex')) main.setAttribute('tabindex', '-1')
    main.focus({ preventScroll: true })
  })
})

export default router
